// Alta de producto en un solo formulario (2026-10-31, Daniela: «crear un producto es muy complejo»).
// La ficha, la caja, el impuesto (con desde cuándo rige) y los precios de venta entran en un paso: antes
// eran cuatro lugares. Las unidades y los impuestos se ELIGEN de listas: escribir «12» en la unidad
// (las unidades por caja) daba «El servidor falló». No se ofrece «envase retornable»: un retornable no
// se compra ni se vende en el ERP todavía (D-83) y marcarlo trabó el ISF 098081179.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { api } from '../api/cliente';
import type { Categoria, ListaPrecio } from '../api/contratos';
import { useCategorias, useListasPrecio, useMarcas } from '../api/consultas';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { hoyIso } from '../ventas/comercial';

export interface ProductoCreado {
  productoId: string;
  presentacionBaseId: string;
  presentacionIds: string[];
  precios?: { guardados: number; error?: string };
}

interface Unidad {
  codigo: string;
  nombre: string;
}
interface Impuesto {
  codigo: string;
  nombre: string;
  tipo: string;
  activo: boolean;
}
interface Sugerencia {
  sugerido: { impuestoCodigo: string; productos: number; categoriaId: string } | null;
}

/** Cómo viene agrupado cuando no se compra suelto. El código de la presentación es la unidad (2.228 de 2.239 «CJ»). */
const AGRUPACIONES: ReadonlyArray<readonly [string, string]> = [
  ['CJ', 'Caja'],
  ['PACK', 'Pack'],
  ['DISPLAY', 'Display'],
  ['BANDEJA', 'Bandeja'],
  ['BULTO', 'Bulto'],
];

const SKU = /^[A-Z0-9][A-Z0-9._-]{1,39}$/;
const ENTERO = /^\d{1,6}$/;
const MONTO = /^\d{1,12}$/;

/** «CERVEZAS › LATA › 470 CC»: la categoría con su rama, para elegirla en una sola lista. */
function rutasDeCategorias(cs: readonly Categoria[]) {
  const porId = new Map(cs.map((c) => [c.id, c]));
  const ruta = (c: Categoria): string => {
    const padre = c.padre_id ? porId.get(c.padre_id) : undefined;
    return padre ? `${ruta(padre)} › ${c.nombre}` : c.nombre;
  };
  return cs
    .map((c) => ({ id: c.id, texto: ruta(c) }))
    .sort((a, b) => a.texto.localeCompare(b.texto));
}

export function NuevoProducto({
  empresaId,
  inicial = {},
  alCreado,
  alCerrar,
}: {
  empresaId: string;
  /** Lo que ya se sabe (p. ej. lo escrito en la línea de la compra). */
  inicial?: { sku?: string; nombre?: string };
  alCreado: (p: ProductoCreado & { sku: string; nombre: string }) => void;
  alCerrar: () => void;
}) {
  const categorias = useCategorias();
  const marcas = useMarcas();
  const listas = useListasPrecio(empresaId);
  const unidades = useQuery({
    queryKey: ['catalogo', 'unidades'],
    queryFn: () => api.get<Unidad[]>('/catalogo/unidades'),
    staleTime: 10 * 60_000,
  });
  const impuestos = useQuery({
    queryKey: ['tributario', 'impuestos'],
    queryFn: () => api.get<Impuesto[]>('/tributario/impuestos'),
    staleTime: 10 * 60_000,
  });
  const intentos = useIntentos();

  const [sku, setSku] = useState((inicial.sku ?? '').toUpperCase());
  const [nombre, setNombre] = useState((inicial.nombre ?? '').toUpperCase());
  const [nombreCorto, setNombreCorto] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [marcaId, setMarcaId] = useState('');
  const [tipo, setTipo] = useState<'producto' | 'servicio'>('producto');
  const [unidad, setUnidad] = useState('UN');
  const [agrupa, setAgrupa] = useState('CJ');
  const [factor, setFactor] = useState('');
  const [impuesto, setImpuesto] = useState('');
  const [impuestoElegido, setImpuestoElegido] = useState(false);
  const [desde, setDesde] = useState(hoyIso());
  const [precios, setPrecios] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const [sinPrecio, setSinPrecio] = useState<{ creado: ProductoCreado; motivo: string } | null>(
    null,
  );

  const sugerencia = useQuery({
    queryKey: ['catalogo', 'impuesto-sugerido', categoriaId, desde],
    queryFn: () =>
      api.get<Sugerencia>('/productos/impuesto-sugerido', { query: { categoriaId, fecha: desde } }),
    enabled: !!categoriaId && tipo === 'producto',
    retry: false,
  });
  const sugerido = sugerencia.data?.sugerido ?? null;
  // La sugerencia llena el impuesto mientras la persona no haya elegido uno.
  useEffect(() => {
    if (!impuestoElegido && sugerido) setImpuesto(sugerido.impuestoCodigo);
  }, [sugerido, impuestoElegido]);

  const opcCategorias = useMemo(() => rutasDeCategorias(categorias.data ?? []), [categorias.data]);
  const adicionales = (impuestos.data ?? []).filter((i) => i.tipo === 'adicional' && i.activo);
  // La API trae una fila por cada empresa de la lista: solo las de esta empresa, una vez cada una.
  const listasVenta = (listas.data ?? []).filter(
    (l: ListaPrecio & { activa?: boolean; empresa_id?: string }, i, todas) =>
      l.uso === 'venta' &&
      l.activa !== false &&
      (l.empresa_id ?? empresaId) === empresaId &&
      todas.findIndex((x) => x.id === l.id) === i,
  );
  const nombreUnidad = (unidades.data ?? []).find((u) => u.codigo === unidad)?.nombre ?? unidad;
  const plural = unidad === 'UN' ? 'unidades' : unidad;
  const nombreAgrupa = AGRUPACIONES.find(([c]) => c === agrupa)?.[1] ?? agrupa;

  const problemas = [
    !SKU.test(sku) && 'SKU: letras mayúsculas, números, punto, guion (2 a 40)',
    nombre.trim().length < 2 && 'el nombre',
    agrupa &&
      factor &&
      (!ENTERO.test(factor) || Number(factor) < 2) &&
      `cuántas unidades trae ${nombreAgrupa.toLowerCase()} (un número desde 2)`,
    ...listasVenta
      .filter((l) => (precios[l.id] ?? '') !== '' && !MONTO.test(precios[l.id]!))
      .map((l) => `el precio de ${l.codigo} (en pesos, sin puntos)`),
  ].filter((x): x is string => !!x);

  const cuerpo = () => ({
    empresaId,
    sku,
    nombre: nombre.trim(),
    ...(nombreCorto.trim() ? { nombreCorto: nombreCorto.trim() } : {}),
    tipo,
    ...(categoriaId ? { categoriaId } : {}),
    ...(marcaId ? { marcaId } : {}),
    unidadBaseCodigo: unidad,
    ...(unidad !== 'UN'
      ? { presentacionBase: { codigo: unidad, nombre: nombreUnidad, unidadCodigo: unidad } }
      : {}),
    presentaciones:
      agrupa && factor
        ? [
            {
              codigo: agrupa,
              nombre: `${nombreAgrupa} de ${Number(factor)}`,
              unidadCodigo: agrupa,
              factorABase: String(Number(factor)),
            },
          ]
        : [],
    impuestos: impuesto ? [{ impuestoCodigo: impuesto, vigenciaDesde: desde }] : [],
    precios: listasVenta
      .filter((l) => MONTO.test(precios[l.id] ?? ''))
      .map((l) => ({ listaId: l.id, valor: precios[l.id]! })),
  });

  const crear = async () => {
    if (problemas.length || enviando) return;
    setError(null);
    setEnviando(true);
    const c = cuerpo();
    try {
      const r = await api.post<ProductoCreado>('/productos', c, {
        idempotencia: intentos.clave('producto-nuevo', c),
      });
      intentos.cerrar();
      if (r.precios?.error) setSinPrecio({ creado: r, motivo: r.precios.error });
      else alCreado({ ...r, sku: c.sku, nombre: c.nombre });
    } catch (e) {
      intentos.cerrar(e);
      setError(e);
    } finally {
      setEnviando(false);
    }
  };

  const teclas = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      void crear();
    }
  };

  if (sinPrecio)
    return (
      <Modal
        titulo="Producto creado"
        alCerrar={() => alCreado({ ...sinPrecio.creado, sku, nombre })}
      >
        <p>
          <strong>
            {sku} · {nombre}
          </strong>{' '}
          quedó creado con su caja e impuesto.
        </p>
        <p className="texto-aviso">
          El precio no quedó: {sinPrecio.motivo}. Ponlo en Configuración › Listas de precio.
        </p>
        <div className="acciones-modal">
          <button
            type="button"
            className="primario"
            onClick={() => alCreado({ ...sinPrecio.creado, sku, nombre })}
          >
            Seguir
          </button>
        </div>
      </Modal>
    );

  return (
    <Modal titulo="Nuevo producto" ancho="ancho" alCerrar={alCerrar}>
      <form
        className="producto-nuevo"
        aria-label="Nuevo producto"
        onSubmit={(e) => {
          e.preventDefault();
          void crear();
        }}
        onKeyDown={teclas}
      >
        <fieldset>
          <legend>Producto</legend>
          <div className="campos">
            <label className="campo corto">
              <span className="etiqueta">SKU *</span>
              <input
                aria-label="SKU"
                value={sku}
                onChange={(e) => setSku(e.target.value.toUpperCase().replace(/\s/g, ''))}
              />
            </label>
            <label className="campo largo">
              <span className="etiqueta">Nombre *</span>
              <input
                aria-label="Nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value.toUpperCase())}
              />
            </label>
            <label className="campo">
              <span className="etiqueta">Nombre corto</span>
              <input
                aria-label="Nombre corto"
                value={nombreCorto}
                maxLength={60}
                onChange={(e) => setNombreCorto(e.target.value.toUpperCase())}
              />
            </label>
            <label className="campo largo">
              <span className="etiqueta">Categoría</span>
              <select
                aria-label="Categoría"
                value={categoriaId}
                onChange={(e) => setCategoriaId(e.target.value)}
              >
                <option value="">—</option>
                {opcCategorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.texto}
                  </option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">Marca</span>
              <select
                aria-label="Marca"
                value={marcaId}
                onChange={(e) => setMarcaId(e.target.value)}
              >
                <option value="">—</option>
                {(marcas.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label className="campo corto">
              <span className="etiqueta">Tipo</span>
              <select
                aria-label="Tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as 'producto' | 'servicio')}
              >
                <option value="producto">Producto</option>
                <option value="servicio">Servicio (sin stock)</option>
              </select>
            </label>
          </div>
        </fieldset>

        {tipo === 'producto' && (
          <fieldset>
            <legend>Cómo se cuenta y cómo viene</legend>
            <div className="campos">
              <label className="campo corto">
                <span className="etiqueta">Se cuenta por</span>
                <select
                  aria-label="Se cuenta por"
                  value={unidad}
                  onChange={(e) => setUnidad(e.target.value)}
                >
                  {(unidades.data ?? [{ codigo: 'UN', nombre: 'Unidad' }]).map((u) => (
                    <option key={u.codigo} value={u.codigo}>
                      {u.codigo} · {u.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <div className="campo largo">
                <span className="etiqueta">Viene también en</span>
                <div className="en-linea">
                  <select
                    aria-label="Viene también en"
                    value={agrupa}
                    onChange={(e) => setAgrupa(e.target.value)}
                  >
                    <option value="">Solo suelto</option>
                    {AGRUPACIONES.map(([c, n]) => (
                      <option key={c} value={c}>
                        {n}
                      </option>
                    ))}
                  </select>
                  {agrupa && (
                    <>
                      <span>de</span>
                      <input
                        aria-label="Unidades que trae"
                        className="numero"
                        inputMode="numeric"
                        placeholder="6"
                        value={factor}
                        onChange={(e) => setFactor(e.target.value.replace(/\D/g, ''))}
                      />
                      <span>{plural}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <p className="ayuda">
              El stock, el costo y el precio van por {nombreUnidad.toLowerCase()}
              {unidad === 'UN' ? ' (la botella, la lata)' : ''}. La cantidad que trae la caja va al
              lado, no en la unidad.
              {agrupa && !factor && ' Sin esa cantidad se crea sin caja (se agrega después).'}
            </p>
          </fieldset>
        )}

        {tipo === 'producto' && (
          <fieldset>
            <legend>Impuesto adicional</legend>
            <div className="campos">
              <label className="campo largo">
                <span className="etiqueta">Impuesto</span>
                <select
                  aria-label="Impuesto"
                  value={impuesto}
                  onChange={(e) => {
                    setImpuesto(e.target.value);
                    setImpuestoElegido(true);
                  }}
                >
                  <option value="">Sin impuesto adicional (solo IVA)</option>
                  {adicionales.map((i) => (
                    <option key={i.codigo} value={i.codigo}>
                      {i.nombre} · {i.codigo}
                    </option>
                  ))}
                </select>
              </label>
              <label className="campo corto">
                <span className="etiqueta">Rige desde</span>
                <input
                  aria-label="Rige desde"
                  type="date"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                />
              </label>
            </div>
            <p className="ayuda">
              {sugerido
                ? `Sugerido por la categoría: lo usan ${sugerido.productos} producto${sugerido.productos === 1 ? '' : 's'} de ella. `
                : categoriaId
                  ? 'La categoría no trae sugerencia: elígelo. '
                  : 'Elige la categoría y se sugiere el que usan sus productos. '}
              Si el producto llegó en una compra anterior, pon la fecha de esa factura.
            </p>
          </fieldset>
        )}

        {listasVenta.length > 0 && (
          <fieldset>
            <legend>Precio de venta por {nombreUnidad.toLowerCase()} (opcional)</legend>
            <div className="campos">
              {listasVenta.map((l) => (
                <label key={l.id} className="campo corto">
                  <span className="etiqueta">
                    {l.codigo} · {l.nombre}
                  </span>
                  <input
                    aria-label={`Precio ${l.codigo}`}
                    className="numero"
                    inputMode="numeric"
                    value={precios[l.id] ?? ''}
                    onChange={(e) =>
                      setPrecios((p) => ({ ...p, [l.id]: e.target.value.replace(/\D/g, '') }))
                    }
                  />
                </label>
              ))}
            </div>
            <p className="ayuda">
              {listasVenta.every((l) => l.semantica === 'bruto')
                ? 'Precio final, con IVA e impuestos incluidos. '
                : ''}
              Las listas que queden vacías se completan después en Listas de precio.
            </p>
          </fieldset>
        )}

        {problemas.length > 0 && (sku || nombre) && (
          <p className="texto-aviso">Falta o está mal: {problemas.join(' · ')}.</p>
        )}
        {error !== null && <PanelError error={error} />}
        <div className="acciones-modal">
          <button type="button" onClick={alCerrar}>
            Volver
          </button>
          <button type="submit" className="primario" disabled={problemas.length > 0 || enviando}>
            {enviando ? 'Creando…' : 'Crear producto (Ctrl+Enter)'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
